import {
  CheckCircleOutlined,
  CloseOutlined,
  QuestionCircleOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  ChannelIdeaEvaluationResult,
  CREATE_EVALUATION_CHANNEL,
  GET_EVALUATION_IDEA_IDS,
  GET_IDEA_RESULTS_BY_ID,
  GET_ME,
  Idea,
  Me,
} from '@frontend/data-access';
import { Comment, DiscussionWall } from '@frontend/feature-discussion';
import { UserAvatar } from '@frontend/shared/ui';
import {
  App,
  Avatar,
  Button,
  Divider,
  Drawer,
  Form,
  Grid,
  Modal,
  Slider,
  Space,
  Tooltip,
} from 'antd';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from '../channel-store/settings/hooks';
import { selectChannelFilters } from '@frontend/feature-idea-channel';
import IdeaEvaluationResult from '../idea-evaluation-result/idea-evaluation-result';
import IdeaEvaluateSkeleton from './skeleton';

const { useBreakpoint } = Grid;

/* eslint-disable-next-line */
export interface IdeaEvaluateProps {
  ideaId: string;
}

export const calculateAverageScoreIdeaChannel = (
  data: ChannelIdeaEvaluationResult[]
) => {
  const average =
    data?.reduce((prev, curr) => prev + curr?.score, 0) / data?.length;
  return isNaN(average) ? undefined : average.toFixed(1);
};

export function IdeaEvaluate({ ideaId }: IdeaEvaluateProps) {
  const { notification } = App.useApp();
  const { t } = useTranslation('common');
  const [form] = Form.useForm();
  const [drawer, setDrawer] = useState(false);
  const [myAverageScore, setMyAverageScore] = useState<number>(0);
  const [isEditing, setIsEditing] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { data: meData } = useQuery<{ me: Me }>(GET_ME);
  const screens = useBreakpoint();
  const channelFilters = useAppSelector(selectChannelFilters);

  const [getData, { data, loading, refetch }] = useLazyQuery<{
    channelIdea: Idea;
  }>(GET_IDEA_RESULTS_BY_ID);
  const handleCompleted = () => {
    notification.success({
      message: t('idea_evaluation_success_message'),
      icon: (
        <span className="text-green-600">
          <CheckCircleOutlined />
        </span>
      ),
    });
    refetch();
    setIsEditing(false);
  };
  const [createEvaluation, { loading: createEvaluationLoading }] = useMutation(
    CREATE_EVALUATION_CHANNEL,
    {
      onCompleted: handleCompleted,
      refetchQueries: [
        {
          query: GET_EVALUATION_IDEA_IDS,
          variables: {
            filterBy: {
              ...channelFilters,
            },
            isEvaluated: true,
            page: 1,
            limit: 1,
          },
        },
        {
          query: GET_EVALUATION_IDEA_IDS,
          variables: {
            filterBy: {
              ...channelFilters,
            },
            isEvaluated: false,
            page: 1,
            limit: 9999,
          },
        },
      ],
    }
  );

  const myResults = useMemo(
    () =>
      data?.channelIdea.channelCriteriaResults.filter(
        (item) => item.createdBy.id === meData?.me.id
      ) || [],
    [data?.channelIdea.channelCriteriaResults, meData?.me.id]
  );

  const reviewers = useMemo(
    () =>
      data?.channelIdea.channelCriteriaResults
        .map((item) => item.createdBy)
        .filter((v, i, a) => a.findIndex((t) => t.id === v.id) === i) || [],
    [data?.channelIdea.channelCriteriaResults]
  );

  const handleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const handleClickSave = () => {
    const values = form.getFieldsValue();

    if (values) {
      createEvaluation({
        variables: {
          input: {
            channelId: data?.channelIdea.channel.id,
            ideaId,
            results: Object.keys(values).map((key) => ({
              criteriaId: key,
              score: values[key],
            })),
          },
        },
      });
    }
  };

  const handleDrawer = () => {
    setDrawer(!drawer);
  };

  const handleFormChange = () => {
    const values = form.getFieldsValue();
    const scores = Object.values(
      values as {
        [key: string]: number;
      }
    ).reduce((prev: number, curr: number) => prev + curr, 0) as number;
    const avg = scores / Object.values(values).length;
    setMyAverageScore(parseFloat(avg.toFixed(1)));
  };

  useEffect(() => {
    if (myResults?.length) {
      form.setFieldsValue(
        myResults?.reduce(
          (prev, curr) => ({
            ...prev,
            [curr.criteria.id]: curr.score,
          }),
          {}
        )
      );
    }
  }, [form, myResults]);

  useEffect(() => {
    getData({
      variables: {
        ideaId,
      },
    });
  }, [getData, ideaId]);

  useEffect(() => {
    if (myResults?.length) {
      setIsEditing(false);
    } else {
      setIsEditing(true);
    }
  }, [myResults]);

  if (loading) return <IdeaEvaluateSkeleton />;
  return (
    <div className="basis-2/5 space-y-3">
      <div className="p-4 rounded-lg bg-gray-200 space-y-3">
        <div>
          <div className="flex justify-between items-center">
            <div className="mb-0 text-gray-700 font-medium text-xs uppercase">
              {t('idea_evaluation_overall_score')}
            </div>
            <Button type="link" onClick={handleModal}>
              {t('idea_evaluation_see_detail')}
            </Button>
          </div>
          <div className="">
            <div className="mb-0 mr-4 text-blue-600 font-medium text-2xl">
              {calculateAverageScoreIdeaChannel(
                data?.channelIdea.channelCriteriaResults || []
              ) || '-'}
            </div>
            {(reviewers?.length || 0) > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-gray-700 font-normal">
                  {t('idea_evaluation_by')}
                </span>
                <Space>
                  <Avatar.Group>
                    {reviewers.map((user, index) => (
                      <UserAvatar key={index} size={32} user={user} />
                    ))}
                  </Avatar.Group>
                </Space>
              </div>
            )}
          </div>
        </div>
        <Divider />
        <div className="flex items-center justify-between gap-2">
          <div className="text-gray-700 font-medium uppercase">
            {t('idea_evaluation_your_evaluate')}
          </div>
          {isEditing ? (
            <div className="text-gray-700 text-sm">
              {t('idea_evaluation_average', { score: myAverageScore })}
            </div>
          ) : (
            <Button type="link" onClick={() => setIsEditing(true)}>
              {t('idea_evaluation_edit')}
            </Button>
          )}
        </div>
        {isEditing ? (
          <div className="space-y-2">
            <Form
              form={form}
              className="space-y-2"
              onFieldsChange={handleFormChange}
              data-test="criterias"
            >
              {data?.channelIdea.channelCriteria.map((item, index) => (
                <div key={index} data-test="slider-row">
                  <div className="flex items-center gap-3">
                    <div className="text-gray-900 font-semibold text-sm">
                      {item.name}
                    </div>
                    <Tooltip title={item.description}>
                      <QuestionCircleOutlined className="cursor-pointer" />
                    </Tooltip>
                  </div>
                  <div className="flex items-center gap-2" data-test="slider">
                    <Form.Item
                      name={item.id}
                      noStyle
                      initialValue={
                        myResults?.find((c) => c.criteria.id === item.id)
                          ?.score || 0
                      }
                    >
                      <Slider
                        min={0}
                        max={10}
                        marks={{
                          0: '0',
                          5: '5',
                          10: '10',
                        }}
                        className="mb-2 flex-grow"
                      />
                    </Form.Item>
                  </div>
                </div>
              ))}
            </Form>
            <div className="text-end space-x-2">
              {myResults?.length > 0 && (
                <Button shape="round" onClick={() => setIsEditing(false)}>
                  {t('idea_evaluation_cancel')}
                </Button>
              )}
              <Button
                type="primary"
                shape="round"
                onClick={handleClickSave}
                loading={createEvaluationLoading}
                data-test="evaluation-save-btn"
              >
                {t('idea_evaluation_save')}
              </Button>
            </div>
          </div>
        ) : (
          <div
            className="text-gray-900 text-base font-semibold"
            data-test="my-score"
          >
            {calculateAverageScoreIdeaChannel(myResults)}
          </div>
        )}
      </div>
      <div
        className="p-4 rounded-lg bg-gray-200 space-y-3 cursor-pointer hover:bg-gray-300"
        onClick={() => {
          handleDrawer();
        }}
      >
        <div className="flex justify-between items-center">
          <div className="mb-0 text-gray-700 font-medium text-xs uppercase">
            {t('idea_evaluation_evaluation_comments')}
          </div>
          <RightOutlined />
        </div>
        {!data?.channelIdea.internalDiscussion?.id ||
        data?.channelIdea.internalDiscussion.comments.edges.length === 0 ? (
          <div>
            <div className="text-gray-700 font-light">
              {t('idea_evaluation_no_comments')}
            </div>
            <div className="text-primary-600">
              {t('idea_evaluation_add_comment')}
            </div>
          </div>
        ) : (
          <Comment
            key={
              data?.channelIdea.internalDiscussion.comments.edges[
                data?.channelIdea.internalDiscussion.comments.edges.length - 1
              ].node.id
            }
            discussionId={data?.channelIdea.internalDiscussion.id}
            comment={
              data?.channelIdea.internalDiscussion.comments.edges[
                data?.channelIdea.internalDiscussion.comments.edges.length - 1
              ].node
            }
            currentUser={meData?.me}
            currentThread={undefined}
            setCurrentThread={() => null}
            hideActions={true}
            showReplies={false}
          />
        )}
      </div>

      <Drawer
        title={
          <div className="flex gap-3 items-center">
            <Button
              ghost
              type="text"
              onClick={handleDrawer}
              icon={<CloseOutlined />}
            />
            {t('idea_evaluation_evaluation_comments')}
          </div>
        }
        placement="right"
        closable={false}
        onClose={handleDrawer}
        open={drawer}
        width={screens.md ? '500' : undefined}
      >
        <DiscussionWall
          id={data?.channelIdea?.internalDiscussion?.id || ''}
          title=" "
        />
      </Drawer>
      <Modal
        title={t('idea_evaluation_expert_scores')}
        open={isModalOpen}
        onCancel={handleModal}
        width={1106}
        footer={false}
        destroyOnClose
      >
        {data && <IdeaEvaluationResult ideaId={data?.channelIdea.id} />}
      </Modal>
    </div>
  );
}

export default IdeaEvaluate;
